import { type ShallowRef, shallowRef } from 'vue';
import { router } from '@/app/router';
import { store } from '@/app/store';
import { portraitGeneration } from '@/services/PortraitGeneration';
import { session } from '@/services/Session';
import {
  messageOf,
  track,
  warnMessageOf,
} from '@/shared/utils/track-telemetry';
import type { PortraitLike } from './useParallelPortraitSlots';
import type { PortraitTelemetry } from './usePortraitTelemetry';

interface OrderSummary {
  groupCount: number;
  individualCount: number;
  totalSum: string;
}

type ReadOrderSummary = () => OrderSummary;

interface PortraitFlowView {
  nextTick: () => Promise<unknown>;
  scrollToNextSlideIfNeeded: () => void;
}

interface UsePortraitFlowOptions {
  applyCounterTheme: () => void;
  getSourcePortrait: (
    portrait: PortraitLike | null | undefined
  ) => PortraitLike | null;
  hasPendingGoPhotoPortraits: () => boolean;
  isParallelDisplaySlotsEnabled: () => boolean;
  isGroupPortraitGalleryEnabled: () => boolean;
  isPrintLockActive: () => boolean;
  restoreSelectionFromPrintingQueue: () => void;
  refreshGroupPortraits: () => void;
  syncDisplaySlotsFromCurrentState: () => void;
  telemetry: PortraitTelemetry;
}

interface RetriablePortrait extends PortraitLike {
  retryCount: number;
}

type GenerationSubscription = readonly [event: string, handler: () => void];

export interface PortraitFlow {
  enter: (view: PortraitFlowView) => Promise<void>;
  isConfirmationPopupOpen: ShallowRef<boolean>;
  isDisposed: () => boolean;
  isGoPhotoGenerationPendingPopupOpen: ShallowRef<boolean>;
  isPrintingLockedPopupOpen: ShallowRef<boolean>;
  isSendReadyPortraitsAvailable: ShallowRef<boolean>;
  launchAdditionalGeneration: () => void;
  leave: () => void;
  onConfirmPrintingClick: (readOrder: ReadOrderSummary) => void;
  onGoPhotoGenerationPendingWaitClick: () => void;
  onNextButtonClick: (readOrder: ReadOrderSummary) => void;
  onPortraitTryAgainClick: (portrait: PortraitLike) => void;
  onSendReadyPortraitsClick: (readOrder: ReadOrderSummary) => void;
}

const SEND_READY_PORTRAITS_DELAY_MS = 30_000;

export function usePortraitFlow({
  applyCounterTheme,
  getSourcePortrait,
  hasPendingGoPhotoPortraits,
  isParallelDisplaySlotsEnabled,
  isGroupPortraitGalleryEnabled,
  isPrintLockActive,
  restoreSelectionFromPrintingQueue,
  refreshGroupPortraits,
  syncDisplaySlotsFromCurrentState,
  telemetry,
}: UsePortraitFlowOptions): PortraitFlow {
  const isConfirmationPopupOpen = shallowRef(false);
  const isGoPhotoGenerationPendingPopupOpen = shallowRef(false);
  const isPrintingLockedPopupOpen = shallowRef(false);
  const isSendReadyPortraitsAvailable = shallowRef(false);
  let disposed = false;
  let isConfirming = false;
  let generationSubscriptions: readonly GenerationSubscription[] | null = null;
  let sendReadyPortraitsTimeout: ReturnType<typeof setTimeout> | null = null;
  // Whether a portrait arrived while this step was on screen. The gallery
  // follows arrivals page by page; a batch that finished before the step
  // (started on the group step) must not throw the visitor to its last page.
  let arrivedDuringStep = false;

  function openGoPhotoGenerationPendingPopup(): void {
    isGoPhotoGenerationPendingPopupOpen.value = true;
    if (
      isSendReadyPortraitsAvailable.value ||
      sendReadyPortraitsTimeout !== null
    ) {
      return;
    }

    sendReadyPortraitsTimeout = setTimeout(() => {
      sendReadyPortraitsTimeout = null;
      if (!disposed && hasPendingGoPhotoPortraits()) {
        isSendReadyPortraitsAvailable.value = true;
      }
    }, SEND_READY_PORTRAITS_DELAY_MS);
  }

  function syncParallelDisplaySlots(): void {
    if (isParallelDisplaySlotsEnabled()) {
      syncDisplaySlotsFromCurrentState();
    }
  }

  function subscribeToGeneration(view: PortraitFlowView): void {
    function onGroupGeneratedAndPreloaded(): void {
      if (!disposed) {
        refreshGroupPortraits();
      }
    }
    function onGeneratedSingle(): void {
      if (disposed) {
        return;
      }
      telemetry.trackPortraitGenerated();
    }

    function onGeneratedAndPreloadedSingle(): void {
      if (disposed) {
        return;
      }
      arrivedDuringStep = true;
      syncParallelDisplaySlots();
      view.scrollToNextSlideIfNeeded();
    }

    function onGeneratedAndPreloadedAll(): void {
      if (disposed) {
        return;
      }
      telemetry.trackAllPortraitsGenerated();
    }

    generationSubscriptions = [
      [
        portraitGeneration.EVENTS.GROUP_GENERATED_AND_PRELOADED,
        onGroupGeneratedAndPreloaded,
      ],
      [portraitGeneration.EVENTS.GENERATED_SINGLE, onGeneratedSingle],
      [
        portraitGeneration.EVENTS.GENERATED_AND_PRELOADED_SINGLE,
        onGeneratedAndPreloadedSingle,
      ],
      [
        portraitGeneration.EVENTS.GENERATED_AND_PRELOADED_ALL,
        onGeneratedAndPreloadedAll,
      ],
    ];

    for (const [event, handler] of generationSubscriptions) {
      portraitGeneration.on(event, handler);
    }
  }

  async function enter(view: PortraitFlowView): Promise<void> {
    syncParallelDisplaySlots();

    if (!store.sessionId) {
      return;
    }

    if (!store.facesSelected || store.facesSelected.length === 0) {
      telemetry.trackMissingFacesRedirect();
      router.redirectToStep('');
      return;
    }

    telemetry.trackStepEntered(() => ({
      facesSelected: store.facesSelected?.length || 0,
      existingPortraits: store.portraits?.length || 0,
    }));
    telemetry.startGeneration();
    subscribeToGeneration(view);

    try {
      await telemetry.withInitialGenerationSpan({
        action: async () => {
          if (
            isGroupPortraitGalleryEnabled() &&
            store.facesSelected.length >= 2
          ) {
            await portraitGeneration.ensureInitialPortraits(
              store.facesSelected
            );
          } else {
            await portraitGeneration.ensureInitialIndividualPortraits(
              store.facesSelected
            );
          }
        },
        facesCount: store.facesSelected?.length || 0,
      });
    } catch (error) {
      const message = messageOf(error);
      // biome-ignore lint/suspicious/noConsole: generation failures remain visible without starting a duplicate request.
      console.warn('[Portraits] Initial generation error:', message);
      if (
        disposed ||
        !store.sessionId ||
        message === 'api_error_missing_session_id'
      ) {
        return;
      }
    }

    if (disposed) {
      return;
    }

    syncParallelDisplaySlots();
    await view.nextTick();
    if (disposed) {
      return;
    }
    if (arrivedDuringStep) {
      view.scrollToNextSlideIfNeeded();
    }

    telemetry.updatePortraitsContext();
    restoreSelectionFromPrintingQueue();
    applyCounterTheme();
  }

  function leave(): void {
    if (disposed) {
      return;
    }
    disposed = true;

    if (sendReadyPortraitsTimeout) {
      clearTimeout(sendReadyPortraitsTimeout);
      sendReadyPortraitsTimeout = null;
    }

    if (generationSubscriptions) {
      for (const [event, handler] of generationSubscriptions) {
        portraitGeneration.off(event, handler);
      }
    }

    telemetry.trackStepLeft();
  }

  function launchAdditionalGeneration(): void {
    // Throws (and stops the batch) once the attempt limit is reached, so the
    // group portrait below is covered by the same limit.
    portraitGeneration.addToQueue(store.facesSelected, { additional: true });
    syncParallelDisplaySlots();
    void portraitGeneration
      .launch({ preload: true })
      .catch((error) =>
        warnMessageOf('[Portraits] Additional generation error:', error)
      );
    launchAdditionalGroupPortrait();
  }

  // A multi-subject session tops up with the group portrait as well: the
  // group scene is what the visitor came for, so a batch of individuals
  // alone reads as broken. The count comes from the same table as the
  // initial batch (a kiosk with multi-subject off, or a single face, gets 0).
  // "+" does not wait for a slow group job, thus a group job in progress
  // gets no second one. A gallery that shows no group (the legacy layout)
  // starts no group job.
  function launchAdditionalGroupPortrait(): void {
    if (
      !isGroupPortraitGalleryEnabled() ||
      portraitGeneration.isGroupGenerating
    ) {
      return;
    }
    const count = portraitGeneration.getGroupPortraitCount(
      store.facesSelected.length,
      { additional: true }
    );
    for (let index = 0; index < count; index += 1) {
      track(
        '[Portraits] Additional group portrait rejected:',
        () => {
          void portraitGeneration
            .generateGroupPortrait(store.facesSelected)
            .catch((error: unknown) =>
              warnMessageOf(
                '[Portraits] Additional group portrait error:',
                error
              )
            );
        },
        warnMessageOf
      );
    }
  }

  function onPortraitTryAgainClick(portrait: PortraitLike): void {
    const sourcePortrait = getSourcePortrait(
      portrait
    ) as RetriablePortrait | null;
    if (!sourcePortrait) {
      return;
    }

    if (sourcePortrait.isRetrying || sourcePortrait.retryCount >= 3) {
      telemetry.trackRetryBlocked(
        sourcePortrait,
        sourcePortrait.isRetrying ? 'already_retrying' : 'max_retries_reached'
      );
      return;
    }

    const retryContext = telemetry.trackRetryAttempt(sourcePortrait);

    requestAnimationFrame((): void => {
      if (disposed) {
        return;
      }
      telemetry.runRetrySpan({
        action: async (): Promise<void> => {
          await portraitGeneration.retry(sourcePortrait, { preload: true });
        },
        portraitUuid: sourcePortrait.uuid,
        retryCount: retryContext.retryCount,
      });
    });
  }

  function onNextButtonClick(readOrder: ReadOrderSummary): void {
    if (store.kiosk?.gophoto_printing) {
      if (hasPendingGoPhotoPortraits()) {
        openGoPhotoGenerationPendingPopup();
        return;
      }
      session.gophotoSendReadyOnly = false;
      telemetry.trackGoPhotoConfirmationSkipped();
      onConfirmPrintingClick(readOrder);
      return;
    }

    telemetry.trackConfirmationOpened(readOrder);
    isConfirmationPopupOpen.value = true;
  }

  function onGoPhotoGenerationPendingWaitClick(): void {
    isGoPhotoGenerationPendingPopupOpen.value = false;
  }

  function onSendReadyPortraitsClick(readOrder: ReadOrderSummary): void {
    if (!isSendReadyPortraitsAvailable.value) {
      return;
    }
    session.gophotoSendReadyOnly = hasPendingGoPhotoPortraits();
    isGoPhotoGenerationPendingPopupOpen.value = false;
    telemetry.trackGoPhotoConfirmationSkipped();
    onConfirmPrintingClick(readOrder);
  }

  function onConfirmPrintingClick(readOrder: ReadOrderSummary): void {
    if (isConfirming) {
      return;
    }
    isConfirming = true;

    if (isPrintLockActive()) {
      isPrintingLockedPopupOpen.value = true;
      isConfirmationPopupOpen.value = false;
      isConfirming = false;
      return;
    }

    telemetry.trackOrderConfirmed(readOrder);
    router.redirectToStep(store.paymentStepLink);
  }

  return {
    enter,
    isConfirmationPopupOpen,
    isDisposed: () => disposed,
    isGoPhotoGenerationPendingPopupOpen,
    isPrintingLockedPopupOpen,
    isSendReadyPortraitsAvailable,
    launchAdditionalGeneration,
    leave,
    onConfirmPrintingClick,
    onGoPhotoGenerationPendingWaitClick,
    onNextButtonClick,
    onPortraitTryAgainClick,
    onSendReadyPortraitsClick,
  };
}
